/**
 * Custom Date Picker Implementation
 * Creates an accessible, customizable date picker with configurable date restrictions
 * Supports multiple instances for quickbuy compatibility
 */
(() => {
  'use strict';

  // Configuration constants
  const CONFIG = {
    MONTH_NAMES: [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ],
    WEEKDAY_NAMES: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    ANIMATION_DURATION: 150,
    MAX_YEARS_AHEAD: 2,
    SELECTORS: {
      container: '[data-date-picker-instance]',
      display: '.date-picker-display',
      input: '.date-picker-input',
      calendar: '.custom-calendar',
      trigger: '.date-picker-trigger'
    },
    CSS_CLASSES: {
      visible: 'visible',
      day: 'day',
      disabled: 'disabled',
      selectable: 'selectable',
      selected: 'selected',
      empty: 'empty',
      blocked: 'blocked',
      outOfRange: 'out-of-range'
    },
    BLOCK_OPTIONS: {
      SATURDAYS: 'saturdays',
      SUNDAYS: 'sundays',
      WEEKENDS: 'weekends'
    },
    US_FEDERAL_HOLIDAYS: {
      // Fixed date holidays (month is 0-indexed)
      FIXED: [
        { month: 0, day: 1, name: "New Year's Day" },
        { month: 5, day: 19, name: 'Juneteenth' },
        { month: 6, day: 4, name: 'Independence Day' },
        { month: 10, day: 11, name: 'Veterans Day' },
        { month: 11, day: 25, name: 'Christmas Day' }
      ],
      // Floating holidays (week: 1-4 for nth occurrence, -1 for last; weekday: 0=Sun, 1=Mon, etc.)
      FLOATING: [
        { month: 0, week: 3, weekday: 1, name: 'MLK Day' },
        { month: 1, week: 3, weekday: 1, name: "Presidents' Day" },
        { month: 4, week: -1, weekday: 1, name: 'Memorial Day' },
        { month: 8, week: 1, weekday: 1, name: 'Labor Day' },
        { month: 9, week: 2, weekday: 1, name: 'Columbus Day' },
        { month: 10, week: 4, weekday: 4, name: 'Thanksgiving' }
      ]
    }
  };

  /**
   * Date utility functions
   */
  const DateUtils = {
    createNormalizedDate(year = null, month = null, day = null) {
      const date = year !== null ? new Date(year, month || 0, day || 1) : new Date();
      date.setHours(0, 0, 0, 0);
      return date;
    },

    formatDisplayDate(date) {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    formatInputDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    isSameDate(date1, date2) {
      return date1.getDate() === date2.getDate() &&
             date1.getMonth() === date2.getMonth() &&
             date1.getFullYear() === date2.getFullYear();
    }
  };

  /**
   * Holiday calculation utilities
   */
  const HolidayCalculator = {
    /**
     * Get nth weekday of month (e.g., 3rd Monday of January)
     * @param {number} year
     * @param {number} month - 0-indexed
     * @param {number} weekday - 0=Sunday, 1=Monday, etc.
     * @param {number} n - Which occurrence (1=first, 2=second, -1=last)
     * @returns {Date}
     */
    getNthWeekdayOfMonth(year, month, weekday, n) {
      if (n === -1) {
        // Last occurrence of weekday in month
        const lastDay = new Date(year, month + 1, 0);
        let date = lastDay.getDate();
        while (lastDay.getDay() !== weekday) {
          lastDay.setDate(--date);
        }
        lastDay.setHours(0, 0, 0, 0);
        return lastDay;
      }

      const firstDay = new Date(year, month, 1);
      let dayOffset = weekday - firstDay.getDay();
      if (dayOffset < 0) dayOffset += 7;

      const nthDate = 1 + dayOffset + (n - 1) * 7;
      return DateUtils.createNormalizedDate(year, month, nthDate);
    },

    /**
     * Apply observed holiday rules (Sat->Fri, Sun->Mon)
     * @param {Date} date
     * @returns {Date}
     */
    applyObservedRule(date) {
      const day = date.getDay();
      if (day === 6) {
        // Saturday -> Friday
        return DateUtils.createNormalizedDate(date.getFullYear(), date.getMonth(), date.getDate() - 1);
      } else if (day === 0) {
        // Sunday -> Monday
        return DateUtils.createNormalizedDate(date.getFullYear(), date.getMonth(), date.getDate() + 1);
      }
      return date;
    },

    /**
     * Calculate all US federal holidays for a given year
     * @param {number} year
     * @param {boolean} applyObserved - Whether to shift holidays when on weekends
     * @returns {Date[]}
     */
    getUSHolidaysForYear(year, applyObserved = true) {
      const holidays = [];

      // Fixed date holidays
      CONFIG.US_FEDERAL_HOLIDAYS.FIXED.forEach(holiday => {
        let date = DateUtils.createNormalizedDate(year, holiday.month, holiday.day);
        if (applyObserved) {
          date = this.applyObservedRule(date);
        }
        holidays.push(date);
      });

      // Floating holidays (already on weekdays, no observed rule needed)
      CONFIG.US_FEDERAL_HOLIDAYS.FLOATING.forEach(holiday => {
        const date = this.getNthWeekdayOfMonth(year, holiday.month, holiday.weekday, holiday.week);
        holidays.push(date);
      });

      return holidays;
    },

    /**
     * Generate holiday set for date range (for efficient O(1) lookup)
     * @param {number} startYear
     * @param {number} endYear
     * @param {boolean} applyObserved
     * @returns {Set<string>} Set of date strings in YYYY-MM-DD format
     */
    generateHolidaySet(startYear, endYear, applyObserved = true) {
      const holidaySet = new Set();

      for (let year = startYear; year <= endYear; year++) {
        const holidays = this.getUSHolidaysForYear(year, applyObserved);
        holidays.forEach(date => {
          holidaySet.add(DateUtils.formatInputDate(date));
        });
      }

      return holidaySet;
    },

    /**
     * Parse custom blocked dates from data attribute
     * @param {string} dataString - Comma-separated dates in YYYY-MM-DD format
     * @returns {Set<string>}
     */
    parseCustomBlockedDates(dataString) {
      const blockedSet = new Set();
      if (!dataString) return blockedSet;

      const dates = dataString.split(',').filter(Boolean);
      dates.forEach(dateStr => {
        const trimmed = dateStr.trim();
        if (trimmed) {
          blockedSet.add(trimmed);
        }
      });

      return blockedSet;
    }
  };

  /**
   * DOM utility functions
   */
  const DOMUtils = {
    createElement(tag, attributes = {}, content = '') {
      const element = document.createElement(tag);
      
      Object.entries(attributes).forEach(([key, value]) => {
        if (key === 'className') {
          element.className = value;
        } else if (key === 'innerHTML') {
          element.innerHTML = value;
        } else {
          element.setAttribute(key, value);
        }
      });

      if (content) {
        element.textContent = content;
      }

      return element;
    },

    forceReflow(element) {
      return element.offsetHeight;
    }
  };

  /**
   * Animation handler using CSS transitions and transitionend events
   */
  class AnimationHandler {
    static show(element, onComplete = null) {
      element.style.display = 'block';
      DOMUtils.forceReflow(element);
      element.classList.add(CONFIG.CSS_CLASSES.visible);
      
      if (onComplete) {
        const computedStyle = window.getComputedStyle(element);
        const transitionDuration = computedStyle.transitionDuration;
        
        if (transitionDuration && transitionDuration !== '0s') {
          const handleTransitionEnd = (e) => {
            if (e.target === element && (e.propertyName === 'opacity' || e.propertyName === 'all')) {
              element.removeEventListener('transitionend', handleTransitionEnd);
              onComplete();
            }
          };
          element.addEventListener('transitionend', handleTransitionEnd);
          
          setTimeout(() => {
            element.removeEventListener('transitionend', handleTransitionEnd);
            onComplete();
          }, CONFIG.ANIMATION_DURATION + 100);
        } else {
          requestAnimationFrame(onComplete);
        }
      }
    }

    static hide(element, onComplete = null) {
      element.classList.remove(CONFIG.CSS_CLASSES.visible);
      
      if (onComplete) {
        const computedStyle = window.getComputedStyle(element);
        const transitionDuration = computedStyle.transitionDuration;
        
        if (transitionDuration && transitionDuration !== '0s') {
          const handleTransitionEnd = (e) => {
            if (e.target === element && (e.propertyName === 'opacity' || e.propertyName === 'all')) {
              element.removeEventListener('transitionend', handleTransitionEnd);
              element.style.display = 'none';
              if (onComplete) onComplete();
            }
          };
          element.addEventListener('transitionend', handleTransitionEnd);
          
          setTimeout(() => {
            element.removeEventListener('transitionend', handleTransitionEnd);
            element.style.display = 'none';
            if (onComplete) onComplete();
          }, CONFIG.ANIMATION_DURATION + 100);
        } else {
          element.style.display = 'none';
          if (onComplete) requestAnimationFrame(onComplete);
        }
      } else {
        const computedStyle = window.getComputedStyle(element);
        const transitionDuration = computedStyle.transitionDuration;
        
        if (transitionDuration && transitionDuration !== '0s') {
          setTimeout(() => {
            element.style.display = 'none';
          }, CONFIG.ANIMATION_DURATION);
        } else {
          element.style.display = 'none';
        }
      }
    }
  }

  /**
   * Calendar state management
   */
  class CalendarState {
    constructor(blockOption, blockUSHolidays = false, observeHolidayShift = true, customBlockedDates = null, blockDaysFromToday = 0) {
      this.today = DateUtils.createNormalizedDate();
      this.currentMonth = this.today.getMonth();
      this.currentYear = this.today.getFullYear();
      this.maxYear = this.today.getFullYear() + CONFIG.MAX_YEARS_AHEAD;
      this.maxDate = DateUtils.createNormalizedDate(this.maxYear, 11, 31);
      this.selectedDate = null;
      this.blockOption = blockOption;
      this.isVisible = false;

      // Block days from today configuration
      this.blockDaysFromToday = parseInt(blockDaysFromToday, 10) || 0;

      // Holiday blocking configuration
      this.blockUSHolidays = blockUSHolidays === 'true' || blockUSHolidays === true;
      this.observeHolidayShift = observeHolidayShift !== 'false' && observeHolidayShift !== false;

      // Pre-calculate holiday sets for performance (O(1) lookup)
      this.holidaySet = new Set();
      this.customBlockedSet = new Set();

      if (this.blockUSHolidays) {
        this.holidaySet = HolidayCalculator.generateHolidaySet(
          this.currentYear,
          this.maxYear,
          this.observeHolidayShift
        );
      }

      if (customBlockedDates) {
        this.customBlockedSet = HolidayCalculator.parseCustomBlockedDates(customBlockedDates);
      }
    }

    isDateInRange(date) {
      return date >= this.today && date <= this.maxDate;
    }

    /**
     * Check if date is within the blocked days from today range
     * @param {Date} date
     * @returns {boolean}
     */
    isDateBlockedFromToday(date) {
      if (this.blockDaysFromToday <= 0) return false;

      const diffTime = date.getTime() - this.today.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      return diffDays >= 0 && diffDays < this.blockDaysFromToday;
    }

    /**
     * Check if date is a US federal holiday
     * @param {Date} date
     * @returns {boolean}
     */
    isHolidayDate(date) {
      if (!this.blockUSHolidays) return false;
      const dateString = DateUtils.formatInputDate(date);
      return this.holidaySet.has(dateString);
    }

    /**
     * Check if date is a custom blocked date
     * @param {Date} date
     * @returns {boolean}
     */
    isCustomBlockedDate(date) {
      if (this.customBlockedSet.size === 0) return false;
      const dateString = DateUtils.formatInputDate(date);
      return this.customBlockedSet.has(dateString);
    }

    isDateBlocked(date) {
      const day = date.getDay();

      // Check days blocked from today (lead time)
      if (this.isDateBlockedFromToday(date)) return true;

      // Check weekend blocking
      switch (this.blockOption) {
        case CONFIG.BLOCK_OPTIONS.SATURDAYS:
          if (day === 6) return true;
          break;
        case CONFIG.BLOCK_OPTIONS.SUNDAYS:
          if (day === 0) return true;
          break;
        case CONFIG.BLOCK_OPTIONS.WEEKENDS:
          if (day === 0 || day === 6) return true;
          break;
      }

      // Check US federal holidays
      if (this.isHolidayDate(date)) return true;

      // Check custom blocked dates
      if (this.isCustomBlockedDate(date)) return true;

      return false;
    }

    navigateMonth(direction) {
      this.currentMonth += direction;

      if (this.currentMonth < 0) {
        this.currentMonth = 11;
        this.currentYear--;
      } else if (this.currentMonth > 11) {
        this.currentMonth = 0;
        this.currentYear++;
      }

      if (this.currentYear < this.today.getFullYear()) {
        this.currentMonth = this.today.getMonth();
        this.currentYear = this.today.getFullYear();
      } else if (this.currentYear > this.maxYear) {
        this.currentMonth = 11;
        this.currentYear = this.maxYear;
      }
    }

    setCurrentMonth(month) {
      this.currentMonth = parseInt(month, 10);
    }

    setCurrentYear(year) {
      this.currentYear = parseInt(year, 10);
    }
  }

  /**
   * Calendar renderer
   */
  class CalendarRenderer {
    constructor(state, onDateSelect, onMonthNavigate) {
      this.state = state;
      this.onDateSelect = onDateSelect;
      this.onMonthNavigate = onMonthNavigate;
    }

    createHeader() {
      const header = DOMUtils.createElement('div', { className: 'calendar-header' });

      const prevBtn = DOMUtils.createElement('button', {
        className: 'calendar-nav-btn',
        'aria-label': 'Previous month',
        innerHTML: '&lsaquo;'
      });
      prevBtn.addEventListener('click', this.handlePrevMonth.bind(this));

      const monthYearSelector = this.createMonthYearSelector();

      const nextBtn = DOMUtils.createElement('button', {
        className: 'calendar-nav-btn',
        'aria-label': 'Next month',
        innerHTML: '&rsaquo;'
      });
      nextBtn.addEventListener('click', this.handleNextMonth.bind(this));

      header.append(prevBtn, monthYearSelector, nextBtn);
      return header;
    }

    createMonthYearSelector() {
      const container = DOMUtils.createElement('div', { className: 'month-year-selector' });

      const monthSelect = this.createMonthSelect();
      const yearSelect = this.createYearSelect();

      container.append(monthSelect, yearSelect);
      return container;
    }

    createMonthSelect() {
      const select = DOMUtils.createElement('select', {
        className: 'month-select',
        'aria-label': 'Select month'
      });

      CONFIG.MONTH_NAMES.forEach((month, index) => {
        const option = DOMUtils.createElement('option', { value: index }, month);
        if (index === this.state.currentMonth) {
          option.selected = true;
        }
        select.appendChild(option);
      });

      select.addEventListener('change', (e) => {
        e.stopPropagation();
        this.state.setCurrentMonth(e.target.value);
        this.onMonthNavigate();
      });

      return select;
    }

    createYearSelect() {
      const select = DOMUtils.createElement('select', {
        className: 'year-select',
        'aria-label': 'Select year'
      });

      for (let year = this.state.today.getFullYear(); year <= this.state.maxYear; year++) {
        const option = DOMUtils.createElement('option', { value: year }, year.toString());
        if (year === this.state.currentYear) {
          option.selected = true;
        }
        select.appendChild(option);
      }

      select.addEventListener('change', (e) => {
        e.stopPropagation();
        this.state.setCurrentYear(e.target.value);
        this.onMonthNavigate();
      });

      return select;
    }

    createWeekdayHeader() {
      const row = DOMUtils.createElement('div', { className: 'weekdays-row' });

      CONFIG.WEEKDAY_NAMES.forEach(day => {
        const dayEl = DOMUtils.createElement('div', { className: 'weekday' }, day);
        row.appendChild(dayEl);
      });

      return row;
    }

    createDaysGrid() {
      const grid = DOMUtils.createElement('div', { className: 'days-grid' });
      const firstDay = new Date(this.state.currentYear, this.state.currentMonth, 1).getDay();
      const daysInMonth = new Date(this.state.currentYear, this.state.currentMonth + 1, 0).getDate();

      for (let i = 0; i < firstDay; i++) {
        const emptyCell = DOMUtils.createElement('div', { 
          className: `${CONFIG.CSS_CLASSES.day} ${CONFIG.CSS_CLASSES.empty}` 
        });
        grid.appendChild(emptyCell);
      }

      for (let day = 1; day <= daysInMonth; day++) {
        const date = DateUtils.createNormalizedDate(this.state.currentYear, this.state.currentMonth, day);
        const dayCell = this.createDayCell(date, day);
        grid.appendChild(dayCell);
      }

      return grid;
    }

    createDayCell(date, day) {
      const isInRange = this.state.isDateInRange(date);
      const isBlocked = this.state.isDateBlocked(date);
      const isHoliday = this.state.isHolidayDate(date) || this.state.isCustomBlockedDate(date);
      const isSelected = this.state.selectedDate && DateUtils.isSameDate(this.state.selectedDate, date);

      let className = CONFIG.CSS_CLASSES.day;

      if (!isInRange) {
        className += ` ${CONFIG.CSS_CLASSES.disabled} ${CONFIG.CSS_CLASSES.outOfRange}`;
      } else if (isBlocked) {
        className += ` ${CONFIG.CSS_CLASSES.disabled} ${CONFIG.CSS_CLASSES.blocked}`;
        // Add holiday class for visual differentiation
        if (isHoliday) {
          className += ' holiday';
        }
      } else {
        className += ` ${CONFIG.CSS_CLASSES.selectable}`;
        if (isSelected) {
          className += ` ${CONFIG.CSS_CLASSES.selected}`;
        }
      }

      const dayCell = DOMUtils.createElement('div', { className }, day.toString());

      if (!isInRange || isBlocked) {
        return dayCell;
      }

      dayCell.addEventListener('click', (e) => {
        e.stopPropagation();
        this.onDateSelect(date);
      });

      return dayCell;
    }

    handlePrevMonth(e) {
      e.stopPropagation();
      this.state.navigateMonth(-1);
      this.onMonthNavigate();
    }

    handleNextMonth(e) {
      e.stopPropagation();
      this.state.navigateMonth(1);
      this.onMonthNavigate();
    }

    render(container) {
      container.innerHTML = '';
      container.append(
        this.createHeader(),
        this.createWeekdayHeader(),
        this.createDaysGrid()
      );
    }
  }

  /**
   * Main DatePicker class
   */
  class DatePicker {
    constructor(container) {
      this.container = container;
      this.uniqueId = container.dataset.datePickerInstance;

      if (!this.validateElements()) {
        return;
      }

      // Extract configuration from data attributes
      const inputElement = this.elements.dateInput;
      const blockOption = inputElement.dataset.blockOption;
      const blockDaysFromToday = inputElement.dataset.blockDaysFromToday;
      const blockUSHolidays = inputElement.dataset.blockUsHolidays;
      const observeHolidayShift = inputElement.dataset.observeHolidayShift;
      const customBlockedDates = inputElement.dataset.customBlockedDates;

      this.state = new CalendarState(
        blockOption,
        blockUSHolidays,
        observeHolidayShift,
        customBlockedDates,
        blockDaysFromToday
      );
      this.renderer = new CalendarRenderer(
        this.state,
        this.selectDate.bind(this),
        this.renderCalendar.bind(this)
      );

      this.init();
    }

    validateElements() {
      this.elements = {
        dateDisplay: this.container.querySelector(CONFIG.SELECTORS.display),
        dateInput: this.container.querySelector(CONFIG.SELECTORS.input),
        calendarContainer: this.container.querySelector(CONFIG.SELECTORS.calendar),
        datePickerTrigger: this.container.querySelector(CONFIG.SELECTORS.trigger)
      };

      return Object.values(this.elements).every(element => element !== null);
    }

    init() {
      this.setupEventListeners();
      this.hideCalendar(false);
    }

    setupEventListeners() {
      this.elements.datePickerTrigger.addEventListener('click', this.handleToggleClick.bind(this));
      this.elements.dateDisplay.addEventListener('click', this.handleToggleClick.bind(this));
      this.elements.calendarContainer.addEventListener('click', (e) => e.stopPropagation());
      this.documentClickHandler = this.handleDocumentClick.bind(this);
    }

    handleToggleClick(e) {
      e.stopPropagation();
      this.toggleCalendar();
    }

    handleDocumentClick() {
      if (this.state.isVisible) {
        this.hideCalendar(true);
      }
    }

    selectDate(date) {
      this.state.selectedDate = date;

      this.elements.dateDisplay.value = DateUtils.formatDisplayDate(date);
      this.elements.dateInput.value = DateUtils.formatInputDate(date);

      this.hideCalendar(true);

      const event = new Event('change', { bubbles: true });
      this.elements.dateInput.dispatchEvent(event);
    }

    showCalendar() {
      this.renderCalendar();
      this.state.isVisible = true;

      AnimationHandler.show(this.elements.calendarContainer, () => {
        requestAnimationFrame(() => {
          document.addEventListener('click', this.documentClickHandler);
        });
      });
    }

    hideCalendar(animate) {
      this.state.isVisible = false;
      document.removeEventListener('click', this.documentClickHandler);

      if (animate) {
        AnimationHandler.hide(this.elements.calendarContainer);
      } else {
        this.elements.calendarContainer.classList.remove(CONFIG.CSS_CLASSES.visible);
        this.elements.calendarContainer.style.display = 'none';
      }
    }

    toggleCalendar() {
      if (this.state.isVisible) {
        this.hideCalendar(true);
      } else {
        this.showCalendar();
      }
    }

    renderCalendar() {
      this.renderer.render(this.elements.calendarContainer);
    }

    destroy() {
      document.removeEventListener('click', this.documentClickHandler);
    }
  }

  /**
   * DatePicker manager for handling multiple instances
   */
  class DatePickerManager {
    constructor() {
      this.instances = new Map();
      this.initializationPromise = null;
    }

    async initialize() {
      if (this.initializationPromise) {
        await this.initializationPromise;
      }

      this.initializationPromise = this.performInitialization();
      const result = await this.initializationPromise;
      return result;
    }

    async forceInitialize() {
      this.initializationPromise = null;
      return this.initialize();
    }

    async performInitialization() {
      try {
        const containers = document.querySelectorAll(CONFIG.SELECTORS.container);
        
        // Clean up orphaned instances
        for (const [id, instance] of this.instances) {
          if (!document.querySelector(`[data-date-picker-instance="${id}"]`)) {
            instance.destroy();
            this.instances.delete(id);
          }
        }
        
        containers.forEach(container => {
          const id = container.dataset.datePickerInstance;
          const isInitialized = container.hasAttribute('data-date-picker-initialized');
          
          if (!isInitialized) {
            if (this.instances.has(id)) {
              this.instances.get(id).destroy();
              this.instances.delete(id);
            }
            
            const instance = new DatePicker(container);
            if (instance.elements) {
              this.instances.set(id, instance);
              container.setAttribute('data-date-picker-initialized', 'true');
            }
          }
        });
      } finally {
        this.initializationPromise = null;
      }
    }

    destroy() {
      this.instances.forEach(instance => instance.destroy());
      this.instances.clear();
    }
  }

  // Create manager instance
  const manager = new DatePickerManager();

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => manager.initialize());
  } else {
    manager.initialize();
  }

  // Re-initialize for Shopify theme editor
  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', () => manager.initialize());
  }

  // Listen for quickbuy events
  document.addEventListener('on:quickbuy:after-open', () => {
    requestAnimationFrame(() => manager.forceInitialize());
  });

  // Observe DOM changes for dynamic content
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        const hasDatePickers = Array.from(mutation.addedNodes).some(node => 
          node.nodeType === 1 && node.querySelector?.(CONFIG.SELECTORS.container)
        );
        
        if (hasDatePickers) {
          requestAnimationFrame(() => manager.initialize());
        }
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Export for global access
  window.DatePicker = DatePicker;
  window.DatePickerManager = manager;
})();